import {
  Box,
  LinearProgress,
  Paper,
  Typography,
  alpha,
  useTheme
} from '@mui/material'
import {
  CheckCircle as CheckCircleIcon,
  ReceiptLong as ReceiptLongIcon,
  Support as SupportIcon,
  BugReport as BugReportIcon,
  Upgrade as UpgradeIcon,
  AccountTree as AccountTreeIcon
} from '@mui/icons-material'

import { linearProgressTintedSx } from '../../utils/dashboardViewConstants'

const StatusRow = ({
  active,
  icon: Icon,
  activeColor,
  loadingLabel,
  doneLabel
}) => {
  const theme = useTheme()
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Icon
        sx={{
          color: active ? activeColor : theme.palette.text.secondary,
          fontSize: 20
        }}
      />
      {active
        ? (
          <LinearProgress variant='indeterminate' sx={linearProgressTintedSx(theme, activeColor)} />
          )
        : (
          <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 22 }} />
          )}
      <Typography variant='caption' sx={{ minWidth: 160, fontWeight: 600, color: theme.palette.text.primary }}>
        {active ? loadingLabel : doneLabel}
      </Typography>
    </Box>
  )
}

const DashboardLoadingStatus = ({
  isLoading,
  vulnUpgradesLoading,
  qboLoading,
  codeTreeLoading = false
}) => {
  const theme = useTheme()
  const st = theme.custom.colors.supportTypes
  const bill = theme.custom.colors.billingDrawer
  const accent = theme.custom?.keysys?.blue || theme.palette.primary.main
  const anyLoading = Boolean(
    isLoading || vulnUpgradesLoading || qboLoading || codeTreeLoading
  )

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: { xs: 16, md: 24 },
        left: { xs: 16, md: 32 },
        zIndex: 1400,
        display: anyLoading ? 'block' : 'none',
        pointerEvents: 'none'
      }}
    >
      <Paper
        elevation={6}
        sx={{
          pointerEvents: 'auto',
          maxWidth: 360,
          p: 1.75,
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.background.paper, 0.98),
          border: theme.custom.colors.dashboard.loadingStatusPaper.border,
          boxShadow: theme.custom.colors.dashboard.loadingStatusPaper.boxShadow
        }}
      >
        <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: 1, color: theme.palette.text.primary }}>
          Loading data
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <StatusRow
            active={isLoading}
            icon={SupportIcon}
            activeColor={st.support}
            loadingLabel='Loading support issues…'
            doneLabel='Support issues loaded'
          />
          <StatusRow
            active={vulnUpgradesLoading}
            icon={BugReportIcon}
            activeColor={st.vulnerability}
            loadingLabel='Loading vulnerabilities…'
            doneLabel='Vulnerabilities loaded'
          />
          <StatusRow
            active={vulnUpgradesLoading}
            icon={UpgradeIcon}
            activeColor={st.upgrade}
            loadingLabel='Loading upgrades…'
            doneLabel='Upgrades loaded'
          />
          <StatusRow
            active={qboLoading}
            icon={ReceiptLongIcon}
            activeColor={bill.accent}
            loadingLabel='Loading QuickBooks lines…'
            doneLabel='QuickBooks data loaded'
          />
          <StatusRow
            active={codeTreeLoading}
            icon={AccountTreeIcon}
            activeColor={accent}
            loadingLabel='Loading SOCR code tree…'
            doneLabel='SOCR code tree loaded'
          />
        </Box>
      </Paper>
    </Box>
  )
}

export { DashboardLoadingStatus }
